import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Swal from 'sweetalert2';
import { Card, CardBody, Button, Input, Select, Spinner, Alert } from '@components/ui';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { useCompanySettings, useUpdateCompanySettings } from '../hooks/useCompany';
import { companyService } from '../services/companyService';
import { formatPhoneDisplay, validatePhoneDGI, PHONE_HELPER_TEXT } from '@/lib/utils/phone';
import { PhoneFormatHelp } from '@/components/PhoneFormatHelp';

// import { useAuthStore } from '@/features/auth/store/authStore';

const companySchema = z.object({
  ruc: z.string().min(1, 'El RUC es requerido').max(20, 'Máximo 20 caracteres'),
  dv: z.string().min(1, 'El DV es requerido').max(2, 'Máximo 2 caracteres'),
  fullName: z.string().min(2, 'Mínimo 2 caracteres').max(200, 'Máximo 200 caracteres'),
  name: z.string().min(2, 'Mínimo 2 caracteres').max(200, 'Máximo 200 caracteres'),
  typeTaxpayer: z.union([
    z.number().min(1, 'El tipo de contribuyente es requerido').max(2),
    z.literal(''),
  ]).refine((val) => val !== '', { message: 'El tipo de contribuyente es requerido' }),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  phone: z.string().max(16, 'Máximo 16 caracteres').superRefine((val, ctx) => {
    const result = validatePhoneDGI(val, { required: true });
    if (!result.valid && result.error) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.error });
    }
  }),
  address: z.string().min(1, 'La dirección es requerida').max(100, 'Máximo 100 caracteres'),
  country: z.string().min(1, 'El país es requerido'),
});

type CompanyFormData = z.infer<typeof companySchema>;

export function EditCompanyPage() {
  const { data: company, isLoading, error } = useCompanySettings();
  const updateMutation = useUpdateCompanySettings();
  // const user = useAuthStore((state) => state.user);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    mode: 'onChange',
    values: {
      ruc: company?.ruc || '',
      dv: company?.dv || '',
      fullName: company?.fullName || '',
      name: company?.name || '',
      typeTaxpayer: company?.typeTaxpayer ? Number(company.typeTaxpayer) : 0,
      email: company?.email || '',
      phone: company?.phone || '',
      address: company?.address || '',
      country: company?.country || '',
    },
  });

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 1MB = 1048576 bytes)
      const maxSize = 1 * 1024 * 1024; // 1MB
      if (file.size > maxSize) {
        await Swal.fire({
          icon: 'error',
          title: 'Archivo muy grande',
          text: 'La imagen no debe ser mayor a 1MB',
          confirmButtonColor: '#ef4444',
        });
        setLogoFile(null);
        setLogoPreview(null);
        e.target.value = ''; // Reset input
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        await Swal.fire({
          icon: 'error',
          title: 'Tipo de archivo inválido',
          text: 'El archivo debe ser una imagen',
          confirmButtonColor: '#ef4444',
        });
        setLogoFile(null);
        setLogoPreview(null);
        e.target.value = ''; // Reset input
        return;
      }

      setUploadError(null);
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: CompanyFormData) => {
    try {
      setUploadError(null);
      let profileImageUrl = company?.profileImage;

      // Upload logo if a new file was selected
      if (logoFile && company?.id) {
        setIsUploading(true);
        try {
          const uploadResult = await companyService.uploadFile(
            logoFile,
            company.id,
            'company'
          );
          profileImageUrl = uploadResult.url;
          console.log('✅ Logo uploaded, URL:', profileImageUrl);
        } catch (uploadErr: any) {
          console.error('Error uploading logo:', uploadErr);
          setIsUploading(false);

          await Swal.fire({
            icon: 'error',
            title: 'Error al subir imagen',
            text: uploadErr?.response?.data?.message || uploadErr?.message || 'Error al subir la imagen. Por favor, intenta de nuevo.',
            confirmButtonColor: '#ef4444',
          });

          return;
        } finally {
          setIsUploading(false);
        }
      }

      // Update company with new data and logo URL
      const updatePayload = {
        ...data,
        email: data.email, // Use company's email
        ...(profileImageUrl && { profileImage: profileImageUrl }),
      };

      console.log('📤 Updating company with payload:', updatePayload);

      await updateMutation.mutateAsync(updatePayload);

      await Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'Información de la empresa actualizada exitosamente',
        confirmButtonColor: '#10b981',
      });

      setLogoFile(null);
      setLogoPreview(null);
    } catch (error: any) {
      console.error('Error updating company:', error);

      const errorMessage = error?.response?.data?.message || error?.message || 'Error al actualizar la información de la empresa';

      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        confirmButtonColor: '#ef4444',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" title="Error al cargar información">
        Ocurrió un error al cargar la información de la empresa. Por favor, intenta de nuevo.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">Editar Empresa</h1>
        <p className="mt-2 text-secondary-600 dark:text-secondary-400">
          Configura la información de tu empresa
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit as any)} className="space-y-6">
        {/* Logo Section */}
        <Card>
          <CardBody>
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-6">
              Logo de la Empresa
            </h2>
            <div className="flex items-start gap-6">
              {/* Logo Preview */}
              <div className="flex-shrink-0">
                <div className="w-32 h-32 border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg overflow-hidden bg-secondary-50 dark:bg-secondary-800 flex items-center justify-center">
                  {logoPreview || company?.profileImage ? (
                    <img
                      src={logoPreview || company?.profileImage || ''}
                      alt="Logo de la empresa"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <PhotoIcon className="w-12 h-12 text-secondary-400" />
                  )}
                </div>
              </div>

              {/* Upload Button */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Cambiar Logo
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                    id="logo-upload"
                    disabled={isUploading}
                  />
                  <label
                    htmlFor="logo-upload"
                    className={`cursor-pointer inline-flex items-center px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg text-sm font-medium text-secondary-700 dark:text-secondary-300 bg-white dark:bg-secondary-800 hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors ${
                      isUploading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <PhotoIcon className="w-5 h-5 mr-2" />
                    {isUploading ? 'Subiendo...' : 'Seleccionar imagen'}
                  </label>
                  {logoFile && !isUploading && (
                    <span className="text-sm text-secondary-600 dark:text-secondary-400">
                      {logoFile.name}
                    </span>
                  )}
                </div>
                {uploadError && (
                  <p className="mt-2 text-sm text-danger-600 dark:text-danger-400">
                    {uploadError}
                  </p>
                )}
                <p className="mt-2 text-xs text-secondary-500 dark:text-secondary-400">
                  Formatos aceptados: JPG, PNG. Tamaño máximo: 1MB
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-6">
              Información Legal
            </h2>
            <div className="space-y-6">
              <Select
                label="Tipo de Contribuyente"
                error={errors.typeTaxpayer?.message}
                options={[
                  { value: '', label: 'Seleccionar tipo de contribuyente...' },
                  { value: '1', label: 'Natural' },
                  { value: '2', label: 'Jurídico' },
                ]}
                {...register('typeTaxpayer', {
                  setValueAs: (v) => v === '' ? '' : Number(v)
                })}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="RUC"
                  placeholder="0000000-0-000000"
                  error={errors.ruc?.message}
                  maxLength={20}
                  {...register('ruc')}
                />
                <div>
                  <Input
                    label="DV"
                    placeholder="00"
                    error={errors.dv?.message}
                    maxLength={2}
                    {...register('dv')}
                  />
                </div>
              </div>

              <Input
                label="Razón Social"
                placeholder="Nombre legal de la empresa"
                error={errors.fullName?.message}
                maxLength={200}
                {...register('fullName')}
              />

              <Input
                label="Nombre Comercial"
                placeholder="Nombre comercial o marca"
                error={errors.name?.message}
                maxLength={200}
                {...register('name')}
              />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-6">
              Información de Contacto
            </h2>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Email"
                  type="email"
                  placeholder="contacto@empresa.com"
                  disabled
                  error={errors.email?.message}
                  {...register('email')}
                />
                <Input
                  label="Teléfono"
                  labelExtra={<PhoneFormatHelp />}
                  placeholder="6789-1234"
                  error={errors.phone?.message}
                  helperText={PHONE_HELPER_TEXT}
                  inputMode="tel"
                  maxLength={16}
                  {...register('phone', {
                    onBlur: (e) => {
                      const formatted = formatPhoneDisplay(e.target.value);
                      if (formatted && formatted !== e.target.value) {
                        setValue('phone', formatted, { shouldValidate: true, shouldDirty: true });
                      } else {
                        trigger('phone');
                      }
                    },
                  })}
                />
              </div>

              <Select
                label="País"
                error={errors.country?.message}
                options={[
                  { value: 'Panamá', label: 'Panamá' },
                  { value: 'Costa Rica', label: 'Costa Rica' },
                  { value: 'Colombia', label: 'Colombia' },
                  { value: 'Otro', label: 'Otro' },
                ]}
                {...register('country')}
              />

              <Input
                label="Dirección"
                placeholder="Dirección completa"
                error={errors.address?.message}
                maxLength={100}
                {...register('address')}
              />
            </div>
          </CardBody>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            data-guide="Guardar Empresa"
            type="submit"
            variant="primary"
            isLoading={updateMutation.isPending || isUploading}
            disabled={isUploading}
          >
            {isUploading ? 'Subiendo imagen...' : 'Guardar Cambios'}
          </Button>
        </div>
      </form>
    </div>
  );
}
